import { Deserializable } from '../protocols/deserializable';
import { AccountType } from './account-type';
import { GoverningJurisdictionType } from '../employers/employer-history/types/governing-jurisdiction-type';
import { InvestmentInstructions } from './investment-instructions';
import { GoverningJurisdictionTypeId } from '../enum/shared/governing-jurisdiction-type-id';
import { MemberAccountStatusIdEnum } from './enum/member-account-status-id.enum';
import { MemberAccountStatus } from './member-account-status';
import { DefinedContributionAccountDetails } from './defined-contribution-account-details';
import { VariableBenefitAccountDetails } from './variable-benefit-account-details';
import { MonthlyPensionAccountDetails } from './monthly-pension-account-details';
import { AccountTypeIdEnum } from '../enum/shared/account-type-id.enum';
import { DateConversionUtils } from '../../utils/date-conversion-utils';

export interface SpousalRolloverAccount {
  spousalAccountId: number;
  spousalAccountIdPrefix: string;
  successorMemberId: number;
  successorName: string;
  spousalAccountStatusId: MemberAccountStatusIdEnum;
}

export interface SpousalRolloverSource {
  sourceAccountId: number;
  sourceAccountIdPrefix: string;
  sourceMemberId: number;
  sourceMemberName: string;
  sourceAccountStatusId: MemberAccountStatusIdEnum;
}

export class MemberAccount implements Deserializable {
  public id!: number;
  public typeId!: AccountTypeIdEnum;
  public type!: AccountType;
  public memberId!: number;
  public member!: string;
  public employerId!: number;
  public employer!: string;
  public statusId!: MemberAccountStatusIdEnum;
  public status!: MemberAccountStatus;
  // TODO: CSS-3941 mocked until the account reads return this
  public statusChangedDate?: string | null;
  public createdDate!: string | null;
  public definedContributionAccountDetails?: DefinedContributionAccountDetails;
  public variableBenefitAccountDetails?: VariableBenefitAccountDetails;
  public monthlyPensionAccountDetails?: MonthlyPensionAccountDetails;
  public governingJurisdictionId!: GoverningJurisdictionTypeId | null;
  public governingJurisdiction!: GoverningJurisdictionType;
  public investmentInstructions!: InvestmentInstructions;
  public totalBalance!: number;
  public spousalRolloverSourceMemberName?: string | null;
  // TODO: CSS-3838 mocked until the reworked spousal VB endpoints land
  public spousalRolloverAccount?: SpousalRolloverAccount | null;
  // TODO: CSS-3840 mocked until the reworked spousal VB endpoints land
  public spousalRolloverSource?: SpousalRolloverSource | null;

  // Not From API
  public displayableAccountTypeName!: string;

  public onDeserialize(): void {
    const Deserialize = window?.injector?.Deserialize;
    if (this.type) {
      this.type = Deserialize?.instanceOf(AccountType, this.type);
      this.displayableAccountTypeName = this.cleanTypeName(this.type.name);
    }

    if (this.governingJurisdiction) {
      this.governingJurisdiction = Deserialize?.instanceOf(GoverningJurisdictionType, this.governingJurisdiction);
    }

    if (this.definedContributionAccountDetails) {
      this.definedContributionAccountDetails = Deserialize?.instanceOf(
        DefinedContributionAccountDetails,
        this.definedContributionAccountDetails
      );
    }

    if (this.variableBenefitAccountDetails) {
      this.variableBenefitAccountDetails = Deserialize?.instanceOf(
        VariableBenefitAccountDetails,
        this.variableBenefitAccountDetails
      );
    }

    if (this.monthlyPensionAccountDetails) {
      this.monthlyPensionAccountDetails = Deserialize?.instanceOf(
        MonthlyPensionAccountDetails,
        this.monthlyPensionAccountDetails
      );
    }

    if (this.investmentInstructions) {
      this.investmentInstructions = Deserialize?.instanceOf(InvestmentInstructions, this.investmentInstructions);
    }

    if (this.status) {
      this.status = Deserialize?.instanceOf(MemberAccountStatus, this.status);
    }
  }

  public isActive(): boolean {
    return this.statusId === MemberAccountStatusIdEnum.Active;
  }

  public isStruck(): boolean {
    return this.statusId === MemberAccountStatusIdEnum.Struck;
  }

  public isInactiveOrStruck(): boolean {
    return [MemberAccountStatusIdEnum.Inactive, MemberAccountStatusIdEnum.Struck].contains(this.statusId);
  }

  public isExhausted(): boolean {
    const effectiveDate = this.variableBenefitAccountDetails?.effectiveDate;
    return (
      this.statusId === MemberAccountStatusIdEnum.Inactive &&
      !!effectiveDate &&
      DateConversionUtils.dateOnlyHasArrived(effectiveDate)
    );
  }

  private cleanTypeName(name: string): string {
    return / Account$/i.test(name) ? name.replace(/ Account$/i, '') : name;
  }
}
